/**
 * @voltxampere/engine — Formula template: BJT common-emitter (S56b)
 *
 * Subgraph: NPN with V_CC → R_C → collector, base driven through
 * R_B by a voltage source, emitter grounded (degenerated or not).
 *
 *     V_CC ── R_C ── collector ⎫
 *     V_in ── R_B ── base       ⎬ BJT NPN
 *                    emitter ── GND
 *
 * MVP output: small-signal voltage gain magnitude |A_v| = g_m R_C
 * with g_m derived from the operating-point I_C (or estimated from
 * V_BE ≈ 0.7 V assumption when no DC result is in hand). Sprint
 * 57+ can layer in the load-line equation; the canonical "CE
 * amplifier" recognition is the high-value piece.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { asNumber, buildNetIndex, componentsOfType, findVoltageSourceDriving, formatLatexValue, isGroundNet, netOf, } from "../helpers.js";
const SYMBOLIC = "A_v = -g_m R_C, \\quad g_m = \\frac{I_C}{V_T}";
export const bjtCommonEmitterTemplate = {
    id: "bjt-common-emitter",
    specificity: "high",
    displayNameKey: {
        tr: "formula.bjt-common-emitter.name",
        en: "formula.bjt-common-emitter.name",
    },
    match(circuit, selected) {
        const bjts = componentsOfType(circuit, "bjt-npn", selected);
        if (bjts.length === 0)
            return null;
        const netIndex = buildNetIndex(circuit);
        for (const q of bjts) {
            const base = netOf(q, "base");
            const collector = netOf(q, "collector");
            const emitter = netOf(q, "emitter");
            if (base === null || collector === null || emitter === null)
                continue;
            if (!isGroundNet(circuit, emitter))
                continue;
            // R_C: between collector and a voltage source.
            const collectorTerminals = netIndex.get(collector) ?? [];
            let rc = null;
            let vcc = null;
            for (const t of collectorTerminals) {
                if (t.component.type !== "resistor")
                    continue;
                const otherNet = netOf(t.component, "p1") === collector
                    ? netOf(t.component, "p2")
                    : netOf(t.component, "p1");
                if (otherNet === null)
                    continue;
                const src = findVoltageSourceDriving(circuit, otherNet);
                if (src !== null) {
                    rc = t.component;
                    vcc = src;
                    break;
                }
            }
            if (rc === null || vcc === null)
                continue;
            // R_B: between base and ANOTHER voltage source.
            const baseTerminals = netIndex.get(base) ?? [];
            let rb = null;
            let vIn = null;
            for (const t of baseTerminals) {
                if (t.component.type !== "resistor")
                    continue;
                const otherNet = netOf(t.component, "p1") === base ? netOf(t.component, "p2") : netOf(t.component, "p1");
                if (otherNet === null)
                    continue;
                const src = findVoltageSourceDriving(circuit, otherNet);
                if (src !== null) {
                    rb = t.component;
                    vIn = src;
                    break;
                }
            }
            if (rb === null || vIn === null)
                continue;
            const rcVal = asNumber(rc.params.resistance);
            const rbVal = asNumber(rb.params.resistance);
            const vInVal = asNumber(vIn.params.voltage);
            if (rcVal === null || rbVal === null || vInVal === null)
                continue;
            // Estimate I_C from the base drive (V_BE ≈ 0.7 V model):
            // I_B = (V_in - 0.7) / R_B, I_C = β · I_B with β = 100 default.
            const vbe = 0.7;
            const beta = asNumber(q.params.beta) ?? 100;
            const ib = (vInVal - vbe) / rbVal;
            if (!Number.isFinite(ib) || ib <= 0)
                continue;
            const ic = beta * ib;
            const vt = 0.02585;
            const gm = ic / vt;
            const av = -gm * rcVal;
            const rcLatex = formatLatexValue(rcVal, "\\Omega");
            const icLatex = formatLatexValue(ic, "A");
            const avLatex = av.toPrecision(3);
            return {
                templateId: "bjt-common-emitter",
                involvedComponents: [q.id, rc.id, rb.id, vcc.id, vIn.id],
                involvedNets: [base, collector, emitter],
                symbolic: SYMBOLIC,
                numeric: {
                    formula: `g_m = \\frac{${icLatex}}{25.85\\,\\text{mV}}, \\quad A_v = -g_m \\cdot ${rcLatex} = ${avLatex}`,
                    result: av,
                    unit: "V/V",
                },
                specificity: "high",
            };
        }
        return null;
    },
};
//# sourceMappingURL=bjt-common-emitter.js.map